"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import { useHydrated } from "@/lib/hydration/use-hydrated";
import type { Locale } from "@/lib/i18n/config";

type RailJob = {
	id: string;
	title: string;
	company: string | null;
	url: string;
};

/** Typed-by-annotation JSON read (no `as` cast); null on a missing/!ok response. */
async function readJson<T>(r: Response | null | undefined): Promise<T | null> {
	if (!r?.ok) return null;
	const body: T = await r.json();
	return body;
}

const US_JOBS_RAIL_FLAG = "us-jobs-rail";

// Spacing lives here, not in the mount sites: a wrapper at each site reserved
// space on every page load for every visitor even when this renders null
// (non-US, flag off), which shifted job-detail pixels (money-visual smoke).
const SURFACE_SPACING: Record<UsJobsRailProps["surface"], string> = {
	home: "mx-auto w-full max-w-6xl px-4 pt-3",
	job_detail: "mt-6",
	jobs_feed: "mt-8",
};

interface UsJobsRailProps {
	locale: Locale;
	/** Which mount point this instance is — feeds the `surface` property on
	 * `us_rail_impression`/`us_rail_click` (jobg8-us-50-floor-2026-09-21.md
	 * Phase 1 M1 funnel). */
	surface: "home" | "job_detail" | "jobs_feed";
}

/**
 * US-only rail surfacing Jobg8 CPC partner inventory that every default
 * surface hides from visitors (`worldwideJobsOnly()` in partner-isolation.ts
 * excludes every country-scoped row). See jobg8-us-50-floor-2026-09-21.md
 * Phase 1.
 *
 * Same fails-closed shape as `<UsCountryHubEntry>`: the flag off, unknown
 * geo, a failed fetch, a non-US code, or zero jobs back from the API all
 * render nothing — no heading, no skeleton left behind [G1].
 *
 * Gated on the `us-jobs-rail` PostHog boolean flag (ENABLED = rail shown),
 * never a hardcoded constant, per CLAUDE.md's feature-flag rule — flipping
 * the flag off in PostHog is the rollback path, no deploy.
 *
 * Client-side for the same reason `<UsCountryHubEntry>` is: the homepage is
 * ISR and the `/jobs` feed and job-detail pages are cached/ISR too, so
 * branching their HTML on visitor country would poison the shared cache.
 * `useHydrated()` keeps the server render and the first client render both
 * null, avoiding a hydration mismatch on a component that renders
 * differently once data arrives.
 */
export function UsJobsRail({ locale, surface }: UsJobsRailProps) {
	const hydrated = useHydrated();
	const posthog = usePostHog();
	const [flagOn, setFlagOn] = useState<boolean | null>(null);
	const [jobs, setJobs] = useState<RailJob[] | null>(null);
	const impressionFired = useRef(false);

	useEffect(() => {
		if (!posthog) return;
		const read = () =>
			setFlagOn(posthog.getFeatureFlag?.(US_JOBS_RAIL_FLAG) === true);
		read();
		return posthog.onFeatureFlags?.(read);
	}, [posthog]);

	useEffect(() => {
		// No locale skip: ~95 US-geo visitors a month land on `/id` pages. The gate
		// is the visitor's real country from `/api/market` (any locale returns it),
		// so an Indonesian visitor on `/id` still resolves "ID" and never triggers
		// the rail fetch. Cost: one small `no-store` request per page view while
		// the flag is on. `market_resolved` can't short-circuit it: on `/id` the
		// market is always `indonesia` whatever the geo, and it carries no country.
		if (flagOn !== true) return;

		let cancelled = false;
		fetch(`/api/market?locale=${locale}`, {
			headers: { accept: "application/json" },
		})
			.then((r) => readJson<{ country?: unknown }>(r))
			.then((data) => {
				if (cancelled || data?.country !== "US") return null;
				return fetch(`/api/jobs/country-rail?country=US&locale=${locale}`, {
					headers: { accept: "application/json" },
				});
			})
			.then((r) => readJson<{ jobs?: RailJob[] }>(r))
			.then((data) => {
				if (cancelled) return;
				if (Array.isArray(data?.jobs) && data.jobs.length > 0) {
					setJobs(data.jobs);
				}
			})
			.catch(() => {
				// Network/edge failure — stay hidden, same rationale as
				// UsCountryHubEntry: a missed impression costs one click; a
				// wrongly-shown one costs click quality on the partner account.
			});
		return () => {
			cancelled = true;
		};
	}, [flagOn, locale]);

	useEffect(() => {
		if (!jobs || jobs.length === 0 || impressionFired.current || !posthog)
			return;
		impressionFired.current = true;
		posthog.capture("us_rail_impression", { surface, count: jobs.length });
	}, [jobs, surface, posthog]);

	if (!hydrated || !jobs || jobs.length === 0) return null;

	return (
		<div className={SURFACE_SPACING[surface]}>
			<section className="rounded-lg border border-border bg-muted/40 px-4 py-3">
				<p className="text-sm font-medium text-foreground mb-2">
					{locale === "id" ? "Lowongan AS untuk Anda" : "US jobs for you"}
				</p>
				<ul className="space-y-1.5">
					{jobs.map((job, index) => (
						<li key={job.id}>
							<a
								href={job.url}
								onClick={() =>
									posthog?.capture("us_rail_click", {
										surface,
										job_id: job.id,
										position: index,
									})
								}
								className="block text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm"
							>
								{job.title}
								{job.company ? ` — ${job.company}` : ""}
							</a>
						</li>
					))}
				</ul>
			</section>
		</div>
	);
}
